import { useEffect, useMemo, useRef, useState } from 'react';
import CloseButton from '../common/CloseButton';
import { useDialogA11y } from '../../hooks/useDialogA11y';

type Gif = { id: string; url: string; preview: string };

export default function GifPicker({ open, onClose, onPick }: { open: boolean; onClose: () => void; onPick: (gif: Gif) => void }) {
  const apiKey = useMemo(() => (import.meta.env.VITE_GIPHY_KEY || 'dc6zaTOxFJmzC'), []); // gitleaks:allow — GIPHY's public beta demo key, not a secret
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<Gif[]>([]);
  const [searchError, setSearchError] = useState(false);

  // GIPHY search via the REST API (the old CDN web component never existed at the pinned version).
  async function runSearch(reset = true) {
    try {
      setLoading(true);
      const limit = 24;
      const offset = reset ? 0 : page * limit;
      const endpoint = q.trim()
        ? `https://api.giphy.com/v1/gifs/search?api_key=${encodeURIComponent(apiKey)}&q=${encodeURIComponent(q)}&limit=${limit}&offset=${offset}&rating=pg-13&lang=en`
        : `https://api.giphy.com/v1/gifs/trending?api_key=${encodeURIComponent(apiKey)}&limit=${limit}&offset=${offset}&rating=pg-13`;
      const res = await fetch(endpoint);
      if (!res.ok) throw new Error(`GIPHY responded ${res.status}`);
      setSearchError(false);
      const data = await res.json();
      const list: Gif[] = (data?.data || []).map((d: any) => {
        const img = d?.images || {};
        return {
          id: String(d?.id || Math.random()),
          url: String(img?.original?.url || img?.downsized?.url || ''),
          preview: String(img?.preview_gif?.url || img?.fixed_height_small?.url || '')
        } as Gif;
      }).filter((g: Gif) => !!g.url);
      if (reset) setResults(list); else setResults(prev => prev.concat(list));
    } catch (e) {
      console.error('GIPHY search failed', e);
      setSearchError(true);
    } finally { setLoading(false); }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- searches when opened; runSearch is recreated every render
  useEffect(() => { if (open) { runSearch(true); } }, [open]);
  const [tab, setTab] = useState<'gifs'|'fav'>('gifs');
  const [autoFav, setAutoFav] = useState<boolean>(() => {
    try { return localStorage.getItem('gifAutoFav') !== '0'; } catch { return true; }
  });
  const [favs, setFavs] = useState<Gif[]>(() => {
    try { const raw = localStorage.getItem('gifFavorites'); return raw ? JSON.parse(raw) : []; } catch { return []; }
  });

  function saveFavs(next: Gif[]) {
    setFavs(next);
    try { localStorage.setItem('gifFavorites', JSON.stringify(next.slice(-50))); } catch {}
  }
  function addFav(g: Gif) {
    const arr = favs.slice();
    if (!arr.find(x => x.id === g.id)) arr.push(g);
    saveFavs(arr.slice(-50));
  }
  function removeFav(id: string) {
    const arr = favs.filter(x => x.id !== id);
    saveFavs(arr);
  }
  useEffect(() => { try { localStorage.setItem('gifAutoFav', autoFav ? '1' : '0'); } catch {} }, [autoFav]);

  const dialogRef = useRef<HTMLDivElement>(null);
  useDialogA11y(open, dialogRef);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-label="GIF picker" tabIndex={-1} className="relative w-full max-w-3xl rounded-xl border border-neutral-800 bg-neutral-900 p-2 shadow-xl outline-none">
        <CloseButton onClick={onClose} className="absolute top-1 right-1" />
        <div className="mb-2 flex items-center gap-3">
          <div className="flex items-center gap-1 rounded border border-neutral-800">
            <button className={`px-3 py-1 ${tab==='gifs'?'bg-neutral-800 text-emerald-300':'text-fg-2 hover:bg-neutral-800/60'}`} onClick={()=>setTab('gifs')}>GIFs</button>
            <button className={`px-3 py-1 ${tab==='fav'?'bg-neutral-800 text-emerald-300':'text-fg-2 hover:bg-neutral-800/60'}`} onClick={()=>setTab('fav')}>Favorites</button>
          </div>
          {tab==='gifs' && (
            <label className="ml-auto flex items-center gap-2 text-xs text-fg-2">
              <input type="checkbox" checked={autoFav} onChange={e=>setAutoFav(e.target.checked)} />
              Add picked GIFs to Favorites
            </label>
          )}
        </div>
        {tab==='gifs' ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <form className="flex-1 min-w-0" onSubmit={(e)=>{ e.preventDefault(); setPage(0); runSearch(true); }}>
                <input className="w-full px-2 py-1 rounded bg-neutral-900 text-fg border border-neutral-800" placeholder="Search GIFs" aria-label="Search GIFs" enterKeyHint="search" value={q} onChange={e=>setQ(e.target.value)} />
                <button type="submit" className="hidden" aria-hidden tabIndex={-1} />
              </form>
              <button className="px-3 py-1 rounded border border-neutral-700 text-fg hover:bg-neutral-800/60" onClick={()=>{ setPage(0); runSearch(true); }}>Search</button>
              <button className="px-3 py-1 rounded border border-neutral-700 text-fg hover:bg-neutral-800/60" onClick={()=>{ setQ(''); setPage(0); runSearch(true); }}>Trending</button>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 max-h-[65vh] overflow-auto p-1">
              {results.map(g => (
                <button key={g.id} className="rounded overflow-hidden border border-neutral-800 hover:border-emerald-600" onClick={()=>{ onPick(g); if (autoFav) addFav(g); onClose(); }}>
                  <img src={g.preview || g.url} alt="gif" className="w-full h-28 object-cover" />
                </button>
              ))}
              {loading && <div className="col-span-full text-center text-fg-2 py-4">Loading...</div>}
              {!loading && results.length===0 && <div className="col-span-full text-center text-fg-2 py-4">{searchError ? 'GIF search is unavailable right now' : 'No results'}</div>}
            </div>
            <div className="flex justify-center">
              <button className="px-3 py-1 rounded border border-neutral-700 text-fg hover:bg-neutral-800/60" disabled={loading} onClick={()=>{ setPage(p=>p+1); runSearch(false); }}>Load more</button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 max-h-[65vh] overflow-auto p-1">
            {favs.length === 0 && (<div className="text-fg-2 text-sm col-span-full p-4">No favorites yet. Pick GIFs with “Add picked GIFs to Favorites” enabled.</div>)}
            {favs.map(g => (
              <div key={g.id} className="relative rounded overflow-hidden border border-neutral-800 hover:border-emerald-600 group">
                <button className="block w-full" onClick={()=>{ onPick(g); onClose(); }}>
                  <img src={g.preview} alt="gif" className="w-full h-24 object-cover" />
                </button>
                <button className="absolute top-1 right-1 text-xs px-1.5 py-0.5 rounded bg-neutral-900/80 border border-neutral-700 text-fg-2 opacity-0 group-hover:opacity-100" onClick={()=>removeFav(g.id)}>Remove</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
